import { StylesProvider, Typography } from '@material-ui/core';
import { default as React, Suspense, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { batch, Provider } from 'react-redux';
import { store } from 'src/common/redux/configure-store';
import { DeviceStatusActions } from 'src/common/redux/reducers/device-status-reducer';
import { ItemListActions } from 'src/common/redux/reducers/item-list-reducer';
import { POSModeActions } from 'src/common/redux/reducers/pos-mode-reducer';
import { ScaleActions } from 'src/common/redux/reducers/scale-reducer';
import { TenderingActions } from 'src/common/redux/reducers/tendering-reducer';
import { TransactionActions } from 'src/common/redux/reducers/transaction-reducer';
import { DynamicComponent } from 'src/common/types/dynamic-comonent-type';
import { EmeraldThemeProvider, ThemeContext } from 'src/emerald-theme-provider';
import CustomerDisplayLocked from './customer-display-locked';
import { CustomerDisplayProxyProps } from './customer-display-proxy';
import CustomerDisplayThankYou from './customer-display-thank-you';
import CustomerDisplayTenderExchange from './customer-display-tender-exchange';
import CustomerIdleDisplay from './customer-idle-display';
import CustomerSalesScreen from './customer-sale-screen-display';
import { CustomerDisplayScreen } from './enum/customer-display.enum';
import useCustomerDisplayData from './hooks/useCustomerDisplayData';
import { ITouchpointTheme } from 'src/common/interfaces/dtos/ITouchpointTheme';
import { TenderExchangeActions } from 'src/common/redux/reducers/tender-exchange-reducer';
import { TouchpointActions } from 'src/common/redux/reducers/touchpoint-reducer';

// Temporary until click functionality is implemented on customer display
const NoClickOverlay: React.FC = ({ children }) => (
  <div className='fullWidth fullHeight' style={{ pointerEvents: 'none' }}>
    {children}
  </div>
);

// All customer display work will break out from this component and will replace what is being returned now
const customerDisplay: DynamicComponent<CustomerDisplayScreen> = {
  LockedScreen: <CustomerDisplayLocked />,
  IdleScreen: <CustomerIdleDisplay />,
  SalesScreen: <CustomerSalesScreen />,
  ThankYou: <CustomerDisplayThankYou />,
  TenderExchange: <CustomerDisplayTenderExchange />,
};

const CustomerDisplayThemeConsumer: React.FC<{ theme?: ITouchpointTheme }> = ({ theme }) => {
  const { setTouchpointTheme } = useContext(ThemeContext);

  useEffect(() => {
    if (theme) {
      setTouchpointTheme(theme);
    }
  }, [theme, setTouchpointTheme]);

  return null;
};

const CustomerDisplay: React.FC = (): JSX.Element => {
  // tying in to customer data changes
  const [customerDisplayData] = useCustomerDisplayData<CustomerDisplayProxyProps>({
    customerDisplay: CustomerDisplayScreen.LockedScreen,
  });

  const [currentScreen, setCurrentScreen] = useState<CustomerDisplayScreen>(
    CustomerDisplayScreen.LockedScreen,
  );

  const { t } = useTranslation();

  useEffect(() => {
    if (customerDisplayData.customerDisplay) {
      setCurrentScreen(customerDisplayData.customerDisplay);
    }
  }, [customerDisplayData.customerDisplay]);

  useEffect(() => {
    batch((): void => {
      if (customerDisplayData.itemState) {
        store.dispatch(ItemListActions.setAllRecords(customerDisplayData.itemState));
      }
      if (customerDisplayData.scale) {
        store.dispatch(ScaleActions.setScaleData(customerDisplayData.scale));
      }
      if (customerDisplayData.deviceStatus) {
        store.dispatch(DeviceStatusActions.setDevices(customerDisplayData.deviceStatus.devices));
      }
      if (customerDisplayData.tenderingInitialState) {
        store.dispatch(TenderingActions.setState(customerDisplayData.tenderingInitialState));
      }
      if (customerDisplayData.POSModeState) {
        store.dispatch(POSModeActions.setState(customerDisplayData.POSModeState));
      }
      if (customerDisplayData.showDepositItems) {
        store.dispatch(TouchpointActions.setShowDepositItems(customerDisplayData.showDepositItems));
      }
      if (customerDisplayData.transactionState) {
        store.dispatch(
          TransactionActions.setTransactionState(customerDisplayData.transactionState),
        );
      }
      if (customerDisplayData.tenderExchange) {
        store.dispatch(
          TenderExchangeActions.setTenderExchangeDetails(
            customerDisplayData.tenderExchange.tenderExchangeDetails,
          ),
        );
      }
    });
  }, [customerDisplayData]);

  return (
    <Suspense fallback={null}>
      <Provider store={store}>
        <EmeraldThemeProvider>
          {/* This needs to be passed to here so its inside the context provider */}
          <CustomerDisplayThemeConsumer theme={customerDisplayData.emeraldTheme} />
          <StylesProvider>
            <NoClickOverlay>
              {customerDisplay[currentScreen] || <Typography>{t('NotImplemented')}</Typography>}
            </NoClickOverlay>
          </StylesProvider>
        </EmeraldThemeProvider>
      </Provider>
    </Suspense>
  );
};

export default CustomerDisplay;
